import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { regionService } from "../api/services/regionService";
import {
  RegionFilters,
  RegionsResponse,
  UpdateRegionPayload,
} from "../schemas/regionSchema";

export const useRegions = (
  filters: RegionFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: regions,
    isLoading: isRegionsLoading,
    refetch,
  } = useQuery<RegionsResponse, Error>({
    queryKey: ["regions", filters],
    queryFn: () => regionService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createRegionMutation = useMutation({
    mutationFn: regionService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      queryClient.invalidateQueries({ queryKey: ["regionOptions"] });
      toast.success("Region created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Region");
    },
  });

  const updateRegionMutation = useMutation({
    mutationFn: regionService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      queryClient.invalidateQueries({ queryKey: ["regionOptions"] });
      toast.success("Region updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Region");
    },
  });

  const deleteRegionMutation = useMutation({
    mutationFn: regionService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      queryClient.invalidateQueries({ queryKey: ["regionOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Region deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Region");
    },
  });

  return {
    regions,
    refetchRegions: refetch,
    createRegion: createRegionMutation.mutate,
    updateRegion: updateRegionMutation.mutate,
    deleteRegions: deleteRegionMutation.mutate,
    isLoading:
      isRegionsLoading ||
      createRegionMutation.isPending ||
      updateRegionMutation.isPending ||
      deleteRegionMutation.isPending,
  };
};
